'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useMemo, useState } from 'react';
import { Canvg } from 'canvg';
import mermaid from 'mermaid';
const DESKTOP_TARGET_LONG_EDGE = 1920;
const MOBILE_TARGET_LONG_EDGE = 1280;
const MERMAID_RENDER_TIMEOUT_MS = 12_000;
const PNG_CONVERT_TIMEOUT_MS = 12_000;
const pngCache = new Map<string, string>();
function parseSvgDimensions(svg: string): {
  width: number;
  height: number;
} {
  const parseNum = (s: string) => Math.round(parseFloat(s) || 0);
  const wMatch = svg.match(/width="([^"]+)"/);
  const hMatch = svg.match(/height="([^"]+)"/);
  const vbMatch = svg.match(/viewBox="([^"]+)"/);
  const vbParts = vbMatch?.[1]?.trim().split(/\s+/) ?? [];
  const width = wMatch ? parseNum(wMatch[1]) : vbParts.length >= 4 ? parseNum(vbParts[2]!) : 800;
  const height = hMatch ? parseNum(hMatch[1]) : vbParts.length >= 4 ? parseNum(vbParts[3]!) : 600;
  return {
    width: width || 800,
    height: height || 600
  };
}
function sanitizeMermaidSource(source: string): string {
  return source.replaceAll('mermaid', '').replaceAll('```', '').replaceAll('\\n', '\n').replaceAll('\\\"', '\\"').replaceAll('[', '["').replaceAll(']', '"]').replaceAll('>|', '>|"').replaceAll('| ', '"|').replaceAll('\\\"', '"').replaceAll('""', '"');
}
function normalizeSvgForXml(svg: string): string {
  return svg.replace(/<br(\s[^/>]*)?>/gi, (_match, attrs = '') => `<br${attrs} />`).replace(/<hr(\s[^/>]*)?>/gi, (_match, attrs = '') => `<hr${attrs} />`);
}
function getTargetLongEdge(): number {
  if (typeof window === 'undefined') {
    return DESKTOP_TARGET_LONG_EDGE;
  }
  return window.innerWidth <= 768 ? MOBILE_TARGET_LONG_EDGE : DESKTOP_TARGET_LONG_EDGE;
}
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(`${label} timed out`));
    }, timeoutMs);
    promise.then(value => {
      window.clearTimeout(timeoutId);
      resolve(value);
    }).catch(error => {
      window.clearTimeout(timeoutId);
      reject(error);
    });
  });
}
async function canvasToPngDataUrl(canvas: HTMLCanvasElement): Promise<string> {
  const blob = await new Promise<Blob | null>(resolve => {
    canvas.toBlob(createdBlob => resolve(createdBlob), 'image/png');
  });
  if (!blob) {
    return canvas.toDataURL('image/png');
  }
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Failed to convert PNG blob to data URL'));
    };
    reader.onerror = () => reject(new Error('Failed to read PNG blob'));
    reader.readAsDataURL(blob);
  });
}
async function svgToPngDataUrl(svgString: string, targetLongEdge: number): Promise<string> {
  const normalizedSvg = normalizeSvgForXml(svgString);

  // Use Image + canvas: browser renders SVG (fonts, styles) correctly.
  // canvg has known issues with @font-face and SVG styles, causing text to not render.
  const svgDataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(normalizedSvg)));

  // Parse dimensions from SVG; scale so longest edge = TARGET_LONG_EDGE
  const {
    width,
    height
  } = parseSvgDimensions(normalizedSvg);
  return new Promise<string>((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      // Use natural dimensions if valid (browser's SVG interpretation); else use parsed
      const srcW = img.naturalWidth || width;
      const srcH = img.naturalHeight || height;
      const srcLong = Math.max(srcW, srcH);
      const outScale = srcLong > 0 ? Math.max(targetLongEdge / srcLong, 1) : 1;
      const finalW = Math.round(srcW * outScale);
      const finalH = Math.round(srcH * outScale);
      const canvas = document.createElement('canvas');
      canvas.width = finalW;
      canvas.height = finalH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('No canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0, finalW, finalH);
      canvasToPngDataUrl(canvas).then(resolve).catch(err => reject(err instanceof Error ? err : new Error('Tainted canvas')));
    };
    img.onerror = () => reject(new Error('Failed to load SVG'));
    img.src = svgDataUrl;
  }).catch(async () => {
    const {
      width,
      height
    } = parseSvgDimensions(normalizedSvg);
    const longEdge = Math.max(width, height);
    const scale = longEdge > 0 ? Math.max(targetLongEdge / longEdge, 1) : targetLongEdge / 800;
    const outputWidth = Math.round(width * scale);
    const outputHeight = Math.round(height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No canvas context');
    ctx.scale(outputWidth / width, outputHeight / height);
    const v = Canvg.fromString(ctx, normalizedSvg);
    await v.render();
    v.stop();
    return canvasToPngDataUrl(canvas);
  });
}
export function useMermaidPng(source) {
  const $ = _c(20);
  const safeSource = typeof source === "string" ? source : "";
  const [pngDataUrl, setPngDataUrl] = useState("");
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const id = useId();
  let t0;
  if ($[0] !== id) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = /:/g;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    t0 = id.replace(t1, "");
    $[0] = id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const diagramId = `mermaid-${t0}-${refreshTrigger}`;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useEffect(_temp, t1);
  let t2;
  if ($[4] !== diagramId || $[5] !== refreshTrigger || $[6] !== safeSource) {
    t2 = () => {
      if (!safeSource.trim()) {
        setPngDataUrl("");
        setError(null);
        return;
      }
      let cancelled = false;
      const renderDiagram = async () => {
        ;
        try {
          const targetLongEdge = getTargetLongEdge();
          setError(null);
          const rawSource = sanitizeMermaidSource(safeSource);
          const cacheKey = `${targetLongEdge}:${rawSource}`;
          if (refreshTrigger === 0) {
            const cached = pngCache.get(cacheKey);
            if (cached) {
              setPngDataUrl(cached);
              return;
            }
          }
          setPngDataUrl("");
          const {
            svg
          } = await withTimeout(mermaid.render(diagramId, rawSource), MERMAID_RENDER_TIMEOUT_MS, "Mermaid render");
          if (cancelled) {
            return;
          }
          const png = await withTimeout(svgToPngDataUrl(svg, targetLongEdge), PNG_CONVERT_TIMEOUT_MS, "PNG conversion");
          if (!cancelled) {
            pngCache.set(cacheKey, png);
            setPngDataUrl(png);
          }
        } catch (t3) {
          const renderError = t3;
          if (!cancelled) {
            setPngDataUrl("");
            setError(renderError instanceof Error ? renderError.message : "Invalid Mermaid syntax.");
          }
        }
      };
      renderDiagram();
      return () => {
        cancelled = true;
      };
    };
    $[4] = diagramId;
    $[5] = refreshTrigger;
    $[6] = safeSource;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== diagramId || $[9] !== safeSource) {
    t3 = [diagramId, safeSource];
    $[8] = diagramId;
    $[9] = safeSource;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  if ($[11] !== error || $[12] !== pngDataUrl || $[13] !== safeSource) {
    t4 = Boolean(safeSource.trim()) && !pngDataUrl && !error;
    $[11] = error;
    $[12] = pngDataUrl;
    $[13] = safeSource;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const loading = t4;
  let t5;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setRefreshTrigger(_temp2);
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const refresh = t5;
  let t6;
  if ($[16] !== error || $[17] !== loading || $[18] !== pngDataUrl) {
    t6 = {
      pngDataUrl,
      error,
      loading,
      refresh
    };
    $[16] = error;
    $[17] = loading;
    $[18] = pngDataUrl;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}
function _temp2(n) {
  return n + 1;
}
function _temp() {
  mermaid.initialize({
    startOnLoad: false,
    theme: "default",
    securityLevel: "strict",
    fontSize: 20
  });
}